import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import type { Editor } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import Paragraph from "@tiptap/extension-paragraph";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Heading from "@tiptap/extension-heading";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Blockquote from "@tiptap/extension-blockquote";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  ALargeSmall,
  Heading2,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  Quote,
  Minus,
  Table2,
  Trash2,
} from "lucide-react";
import { Extension } from "@tiptap/core";
import { useBranding } from "../../../branding/BrandingProvider";
import { FontSizeMark } from "./tiptap/FontSizeMark";
import { TextColorMark } from "./tiptap/TextColorMark";
import { LineHeightExtension } from "./tiptap/LineHeightExtension";
import { HardBreakNode } from "./tiptap/HardBreakNode";

/**
 * Single-line fields (headings, CTA labels, ...) must never split into
 * paragraphs — plain Enter inserts a line break instead. Document-mode
 * fields keep Enter = new paragraph (Shift+Enter for a break).
 */
const InlineEnterAsBreak = Extension.create({
  name: "inlineEnterAsBreak",
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.insertContent({ type: "hardBreak" }),
    };
  },
});
import "@sections/richtext.css";

interface InlineEditOverlayProps {
  /** The DOM element we are overlaying on top of. */
  targetEl: HTMLElement;
  /** The prop name being edited. */
  propName: string;
  /** Current value of the prop. */
  value: string;
  /** Called whenever the editor content changes. */
  onChange: (html: string) => void;
  /** Called to close the overlay (e.g. on blur or Escape). */
  onClose: () => void;
}

interface OverlayRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Props that get the document-mode editor (multi-paragraph, tables,
 * lists, headings, blockquotes). All other props use the simpler
 * single-paragraph inline editor.
 */
const DOCUMENT_MODE_PROPS = new Set(["body", "bodyRight", "subheading"]);

/**
 * Effective CSS zoom applied to an element (the canvas scales down via
 * `zoom` when the workspace is narrow). getBoundingClientRect returns
 * visual pixels, but CSS top/left set INSIDE the zoomed subtree are
 * re-multiplied by the zoom — so measured offsets must be divided by it.
 */
function effectiveZoom(el: HTMLElement, visualWidth: number): number {
  const native = (el as HTMLElement & { currentCSSZoom?: number })
    .currentCSSZoom;
  if (typeof native === "number" && native > 0) return native;
  // Fallback: layout width (CSS px) vs visual width ratio
  return el.offsetWidth > 0 ? visualWidth / el.offsetWidth : 1;
}

function measureOverlayRect(targetEl: HTMLElement): OverlayRect | null {
  const sectionContainer = targetEl.closest("[data-section-id]");
  if (!sectionContainer) return null;
  const containerRect = sectionContainer.getBoundingClientRect();
  const targetRect = targetEl.getBoundingClientRect();
  const zoom = effectiveZoom(targetEl, targetRect.width);
  return {
    top: (targetRect.top - containerRect.top) / zoom,
    left: (targetRect.left - containerRect.left) / zoom,
    width: targetRect.width / zoom,
    height: targetRect.height / zoom,
  };
}

/**
 * Strips wrapper <p> tags that TipTap adds so the output is inline HTML.
 * Preserves any line-height class (lh-tight/lh-normal/lh-loose) by
 * converting it to a wrapping <span> — line-height is inherited so it
 * still takes effect inside the parent block element.
 */
function unwrapParagraph(html: string): string {
  const trimmed = html.trim();
  const match = trimmed.match(/^<p(\s[^>]*)?>(.+)<\/p>$/s);
  if (!match || match[2].includes("<p")) return trimmed;

  const inner = match[2];
  const attrs = match[1] || "";

  // Check for a line-height class on the <p> tag
  const lhMatch = attrs.match(/\blh-(tight|normal|loose)\b/);
  if (lhMatch) {
    return `<span class="lh-${lhMatch[1]}">${inner}</span>`;
  }

  return inner;
}

/**
 * Converts a stored value (inline HTML) into TipTap paragraph content.
 * If the value uses a <span class="lh-*"> wrapper (produced by
 * unwrapParagraph), the line-height class is lifted onto the <p> tag
 * so TipTap's LineHeightExtension can parse it as a paragraph attribute.
 */
function wrapForEditor(value: string): string {
  if (!value) return "<p></p>";
  // Fold paragraph splits stored by the old editor (Enter used to create
  // paragraphs inside single-line fields) into hard breaks, so reopening
  // and saving the field heals the value.
  if (/<p[\s>]/.test(value)) {
    value = value
      .replace(/\s*<\/p>\s*<p[^>]*>\s*/g, "<br>")
      .replace(/^\s*<p[^>]*>/, "")
      .replace(/<\/p>\s*$/, "");
  }
  const lhMatch = value.match(
    /^<span class="lh-(tight|normal|loose)">([\s\S]+)<\/span>$/,
  );
  if (lhMatch) {
    return `<p class="lh-${lhMatch[1]}">${lhMatch[2]}</p>`;
  }
  return `<p>${value}</p>`;
}

/** Font size options for the segmented control. */
const FONT_SIZES = [
  { key: "s", label: "S" },
  { key: "m", label: "M" },
  { key: "l", label: "L" },
  { key: "xl", label: "XL" },
] as const;

/** Text color options for the swatch row. */
const TEXT_COLORS = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "tertiary", label: "Tertiary" },
  { key: "text", label: "Text" },
  { key: "muted", label: "Muted" },
  { key: "light", label: "Light" },
  { key: "dark", label: "Dark" },
] as const;

/** Line height options that cycle through. */
const LINE_HEIGHTS = [
  { key: "tight", label: "Tight" },
  { key: "normal", label: "Normal" },
  { key: "loose", label: "Loose" },
] as const;

// ── Shared button classes ──────────────────────────────────────────────

const btnBase =
  "flex items-center justify-center w-[28px] h-[26px] rounded-[6px] border-0 bg-transparent transition-colors cursor-pointer";
const btnInactive = `${btnBase} text-ink-3 hover:bg-paper-2 hover:text-ink-1`;
const btnActive = `${btnBase} bg-tide-ss text-tide`;

/** Smaller button for font-size segmented control. */
const sizeBtn =
  "flex items-center justify-center h-[22px] min-w-[24px] px-1 rounded-[5px] border-0 bg-transparent transition-colors cursor-pointer text-[10px] font-bold leading-none";
const sizeBtnInactive = `${sizeBtn} text-ink-4 hover:bg-paper-2 hover:text-ink-2`;
const sizeBtnActive = `${sizeBtn} bg-tide-ss text-tide`;

/** Text-label button for table operations. */
const textBtn =
  "flex items-center justify-center h-[22px] px-1.5 rounded-[5px] border-0 bg-transparent transition-colors cursor-pointer text-[10px] font-semibold leading-none";
const textBtnInactive = `${textBtn} text-ink-3 hover:bg-paper-2 hover:text-ink-1`;
const textBtnDanger = `${textBtn} text-ink-4 hover:bg-rust-s hover:text-rust`;

const toolbarChrome =
  "flex items-center gap-0.5 bg-paper-hi rounded-[9px] px-1 py-[3px] shadow-card border border-ink-6";

const divider = "w-px h-[16px] bg-ink-6 mx-0.5";

// ── Document-mode toolbar ──────────────────────────────────────────────

/**
 * Fixed toolbar for document-mode editing: headings, lists, blockquote,
 * table, and horizontal rule. Appears above the editor overlay.
 */
function DocumentToolbar({ editor }: { editor: Editor }) {
  const isInTable = editor.isActive("table");

  return (
    <div
      data-editor-chrome
      className="absolute bottom-full left-0 mb-2 flex flex-col items-start gap-1"
      style={{ zoom: "var(--canvas-unzoom, 1)" }}
    >
      {/* Block format row */}
      <div className={toolbarChrome}>
        {/* Heading toggles */}
        <button
          type="button"
          className={
            editor.isActive("heading", { level: 2 }) ? btnActive : btnInactive
          }
          title="Heading 2"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleHeading({ level: 2 }).run();
          }}
        >
          <Heading2 size={13} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          className={
            editor.isActive("heading", { level: 3 }) ? btnActive : btnInactive
          }
          title="Heading 3"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
        >
          <Heading3 size={13} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          className={
            editor.isActive("heading", { level: 4 }) ? btnActive : btnInactive
          }
          title="Heading 4"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleHeading({ level: 4 }).run();
          }}
        >
          <Heading4 size={13} strokeWidth={2.5} />
        </button>

        <div className={divider} />

        {/* List toggles */}
        <button
          type="button"
          className={
            editor.isActive("bulletList") ? btnActive : btnInactive
          }
          title="Bullet list"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleBulletList().run();
          }}
        >
          <List size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          className={
            editor.isActive("orderedList") ? btnActive : btnInactive
          }
          title="Ordered list"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleOrderedList().run();
          }}
        >
          <ListOrdered size={14} strokeWidth={2} />
        </button>

        <div className={divider} />

        {/* Blockquote */}
        <button
          type="button"
          className={
            editor.isActive("blockquote") ? btnActive : btnInactive
          }
          title="Blockquote"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().toggleBlockquote().run();
          }}
        >
          <Quote size={13} strokeWidth={2.5} />
        </button>

        <div className={divider} />

        {/* Table insert */}
        <button
          type="button"
          className={isInTable ? btnActive : btnInactive}
          title={isInTable ? "Cursor is in a table" : "Insert 3x3 table"}
          onMouseDown={(e) => {
            e.preventDefault();
            if (!isInTable) {
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run();
            }
          }}
        >
          <Table2 size={13} strokeWidth={2.5} />
        </button>

        {/* Horizontal rule */}
        <button
          type="button"
          className={btnInactive}
          title="Horizontal rule"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().setHorizontalRule().run();
          }}
        >
          <Minus size={14} strokeWidth={2.5} />
        </button>
      </div>

      {/* Table operations row (only when cursor is inside a table) */}
      {isInTable && (
        <div className={toolbarChrome}>
          <span className="text-[10px] font-medium text-ink-4 px-1 select-none">
            Table
          </span>

          <div className={divider} />

          <button
            type="button"
            className={textBtnInactive}
            title="Add row before"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().addRowBefore().run();
            }}
          >
            + Row before
          </button>
          <button
            type="button"
            className={textBtnInactive}
            title="Add row after"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().addRowAfter().run();
            }}
          >
            + Row after
          </button>
          <button
            type="button"
            className={textBtnInactive}
            title="Add column before"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().addColumnBefore().run();
            }}
          >
            + Col before
          </button>
          <button
            type="button"
            className={textBtnInactive}
            title="Add column after"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().addColumnAfter().run();
            }}
          >
            + Col after
          </button>

          <div className={divider} />

          <button
            type="button"
            className={textBtnInactive}
            title="Delete row"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().deleteRow().run();
            }}
          >
            - Row
          </button>
          <button
            type="button"
            className={textBtnInactive}
            title="Delete column"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().deleteColumn().run();
            }}
          >
            - Col
          </button>

          <div className={divider} />

          <button
            type="button"
            className={textBtnDanger}
            title="Delete entire table"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().deleteTable().run();
            }}
          >
            <Trash2 size={11} strokeWidth={2.5} className="mr-0.5" />
            Table
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────

/**
 * Positions a TipTap editor directly over a [data-prop] element on
 * the canvas. Copies the target element's computed font styles so the
 * editing experience feels truly inline.
 *
 * When the prop is a "document mode" prop (body, bodyRight), the editor
 * includes extended extensions for tables, lists, headings, blockquotes,
 * and horizontal rules, plus a fixed block-format toolbar.
 */
export function InlineEditOverlay({
  targetEl,
  propName,
  value,
  onChange,
  onClose,
}: InlineEditOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<OverlayRect | null>(null);
  const [fontStyles, setFontStyles] = useState<React.CSSProperties>({});
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmittedRef = useRef(value);
  const { branding } = useBranding();

  const isDocumentMode = DOCUMENT_MODE_PROPS.has(propName);

  const extensions = useMemo(
    () => [
      Document,
      Text,
      Paragraph,
      Bold,
      Italic,
      Underline,
      LinkExtension.configure({ openOnClick: false, autolink: false }),
      FontSizeMark,
      TextColorMark,
      LineHeightExtension,
      HardBreakNode,
      ...(isDocumentMode
        ? [
            Heading.configure({ levels: [2, 3, 4] as const }),
            BulletList,
            OrderedList,
            ListItem,
            Blockquote,
            HorizontalRule,
            Table.configure({ resizable: false }),
            TableRow,
            TableCell,
            TableHeader,
          ]
        : [InlineEnterAsBreak]),
    ],
    [isDocumentMode],
  );

  const initialContent = isDocumentMode
    ? value || "<p></p>"
    : wrapForEditor(value);

  const editor = useEditor({
    extensions,
    content: initialContent,
    autofocus: "end",
    editorProps: {
      attributes: {
        class: isDocumentMode ? "outline-none rich-text" : "outline-none",
      },
    },
    onUpdate({ editor: ed }) {
      const html = isDocumentMode
        ? ed.getHTML()
        : unwrapParagraph(ed.getHTML());
      lastEmittedRef.current = html;
      onChangeRef.current(html);
    },
  });

  // Measure the target element and copy its font styles
  useLayoutEffect(() => {
    const sectionContainer = targetEl.closest("[data-section-id]");
    if (!sectionContainer) return;

    setRect(measureOverlayRect(targetEl));

    const computed = window.getComputedStyle(targetEl);
    setFontStyles({
      fontFamily: computed.fontFamily,
      fontSize: computed.fontSize,
      fontWeight: computed.fontWeight,
      fontStyle: computed.fontStyle,
      lineHeight: computed.lineHeight,
      letterSpacing: computed.letterSpacing,
      textAlign: computed.textAlign as React.CSSProperties["textAlign"],
      color: computed.color,
      textTransform:
        computed.textTransform as React.CSSProperties["textTransform"],
    });

    // Hide the original element so it does not show through. A per-keystroke
    // canvas re-render can REPLACE the target node (losing any inline style
    // set on it), so hide via a selector-scoped style tag that also covers
    // replacement nodes, with the inline style as a same-frame stopgap.
    targetEl.style.visibility = "hidden";
    const sectionId = sectionContainer.getAttribute("data-section-id");
    const dataProp = targetEl.getAttribute("data-prop");
    let styleTag: HTMLStyleElement | null = null;
    if (sectionId && dataProp) {
      styleTag = document.createElement("style");
      styleTag.textContent = `[data-section-id="${CSS.escape(sectionId)}"] [data-prop="${CSS.escape(dataProp)}"] { visibility: hidden !important; }`;
      document.head.appendChild(styleTag);
    }

    return () => {
      targetEl.style.visibility = "";
      styleTag?.remove();
    };
  }, [targetEl]);

  // Re-measure on window resize (also fires when the canvas zoom changes,
  // since zoom follows the workspace width)
  useEffect(() => {
    function handleResize() {
      setRect((prev) => measureOverlayRect(targetEl) ?? prev);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [targetEl]);

  // Handle Escape to close
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Sync external value changes (e.g. inspector edits)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value !== lastEmittedRef.current) {
      const content = isDocumentMode
        ? value || "<p></p>"
        : wrapForEditor(value);
      editor.commands.setContent(content, { emitUpdate: false });
      lastEmittedRef.current = value;
    }
  }, [value, editor, isDocumentMode]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      // Don't close if focus stays within the overlay (e.g. toolbar click)
      const related = e.relatedTarget as HTMLElement | null;
      if (
        related &&
        overlayRef.current &&
        overlayRef.current.contains(related)
      ) {
        return;
      }
      // Small delay so BubbleMenu button clicks can register
      setTimeout(() => {
        if (
          overlayRef.current &&
          !overlayRef.current.contains(document.activeElement)
        ) {
          onClose();
        }
      }, 150);
    },
    [onClose],
  );

  const toggleLink = useCallback(() => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  /** Resolve a text-color key to its actual CSS color value for swatches. */
  const swatchColor = useCallback(
    (key: string): string => {
      switch (key) {
        case "primary":
          return branding?.primaryColor || "#b83333";
        case "secondary":
          return branding?.secondaryColor || "#2E8B8B";
        case "tertiary":
          return branding?.tertiaryColor || "#1e3a5f";
        case "text":
          return "#2c3e50";
        case "muted":
          return "#6c757d";
        case "light":
          return "#ffffff";
        case "dark":
          return "#1a1a1a";
        default:
          return "#888";
      }
    },
    [branding],
  );

  /** Get the currently active font size key, or null. */
  const activeFontSize = editor
    ? FONT_SIZES.find((s) =>
        editor.isActive("fontSize", { size: s.key }),
      )?.key ?? null
    : null;

  /** Get the currently active text color key, or null. */
  const activeTextColor = editor
    ? TEXT_COLORS.find((c) =>
        editor.isActive("textColor", { color: c.key }),
      )?.key ?? null
    : null;

  /** Get the currently active line height, or null. */
  const activeLineHeight = editor
    ? LINE_HEIGHTS.find((lh) => {
        // Check paragraph-level attribute
        return editor.getAttributes("paragraph").lineHeight === lh.key;
      })?.key ?? null
    : null;

  /** Cycle line height: null -> tight -> normal -> loose -> null */
  const cycleLineHeight = useCallback(() => {
    if (!editor) return;
    const order = [null, "tight", "normal", "loose"] as const;
    const currentIdx = order.indexOf(
      activeLineHeight as (typeof order)[number],
    );
    const nextIdx = (currentIdx + 1) % order.length;
    const next = order[nextIdx];
    if (next) {
      editor.chain().focus().setLineHeight(next).run();
    } else {
      editor.chain().focus().unsetLineHeight().run();
    }
  }, [editor, activeLineHeight]);

  if (!rect || !editor) return null;

  return (
    <div
      ref={overlayRef}
      data-editor-chrome
      className="absolute z-[10]"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        minHeight: rect.height,
        // Copy the target's computed text styles for both modes so the
        // editor renders identically to the displayed content. In document
        // mode this base still drives .rich-text's heading/list sizing, which
        // is em-relative (see richtext.css) — so headings scale proportionally
        // from the copied base font-size, and paragraphs inherit the copied
        // text-align. Dropping these was making document-mode text blocks
        // (body/bodyRight/subheading) shrink to 16px and lose center
        // alignment the moment they were selected for editing.
        ...fontStyles,
      }}
      onBlur={handleBlur}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Focus ring */}
      <div className="absolute -inset-[3px] rounded-[4px] border-2 border-tide/50 pointer-events-none" />

      {/* Document-mode block toolbar (fixed above the editor) */}
      {isDocumentMode && <DocumentToolbar editor={editor} />}

      {/* Floating format toolbar (appears on text selection) */}
      <BubbleMenu
        editor={editor}
        options={{
          placement: "top",
          offset: 8,
        }}
      >
        <div
          data-editor-chrome
          className="flex items-center gap-0.5 bg-paper-hi rounded-[9px] px-1 py-[3px] shadow-card border border-ink-6"
          style={{ zoom: "var(--canvas-unzoom, 1)" }}
        >
          {/* Bold / Italic / Underline / Link */}
          <button
            type="button"
            className={editor.isActive("bold") ? btnActive : btnInactive}
            title="Bold"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBold().run();
            }}
          >
            <BoldIcon size={13} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className={editor.isActive("italic") ? btnActive : btnInactive}
            title="Italic"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleItalic().run();
            }}
          >
            <ItalicIcon size={13} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className={editor.isActive("underline") ? btnActive : btnInactive}
            title="Underline"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleUnderline().run();
            }}
          >
            <UnderlineIcon size={13} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className={editor.isActive("link") ? btnActive : btnInactive}
            title="Link"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleLink();
            }}
          >
            <LinkIcon size={13} strokeWidth={2.5} />
          </button>

          {/* Divider */}
          <div className={divider} />

          {/* Font size segmented control */}
          <div className="flex items-center gap-px bg-paper-2 rounded-[6px] px-0.5 py-[2px]">
            {FONT_SIZES.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={
                  activeFontSize === key ? sizeBtnActive : sizeBtnInactive
                }
                title={`Font size: ${label}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (activeFontSize === key) {
                    editor.chain().focus().unsetFontSize().run();
                  } else {
                    editor.chain().focus().setFontSize(key).run();
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className={divider} />

          {/* Text color swatches */}
          <div className="flex items-center gap-[3px] px-0.5">
            {/* Default (reset) swatch */}
            <button
              type="button"
              className="w-[18px] h-[18px] rounded-full border-0 cursor-pointer transition-all p-0 flex items-center justify-center"
              style={{
                background:
                  "conic-gradient(from 0deg, #ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)",
                boxShadow:
                  activeTextColor === null
                    ? "0 0 0 2px var(--color-tide)"
                    : undefined,
              }}
              title="Default color (reset)"
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().unsetTextColor().run();
              }}
            >
              {activeTextColor === null && (
                <span className="block w-[8px] h-[8px] rounded-full bg-paper-hi" />
              )}
            </button>
            {TEXT_COLORS.map(({ key, label }) => {
              const color = swatchColor(key);
              const isActive = activeTextColor === key;
              const isLight = key === "light";
              return (
                <button
                  key={key}
                  type="button"
                  className="w-[18px] h-[18px] rounded-full border-0 cursor-pointer transition-all p-0"
                  style={{
                    backgroundColor: color,
                    boxShadow: isActive
                      ? "0 0 0 2px var(--color-tide)"
                      : undefined,
                    border: isLight
                      ? "1.5px solid var(--color-ink-6)"
                      : "1.5px solid transparent",
                  }}
                  title={label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (isActive) {
                      editor.chain().focus().unsetTextColor().run();
                    } else {
                      editor.chain().focus().setTextColor(key).run();
                    }
                  }}
                />
              );
            })}
          </div>

          {/* Divider */}
          <div className={divider} />

          {/* Line height cycle button */}
          <button
            type="button"
            className={activeLineHeight ? btnActive : btnInactive}
            title={
              activeLineHeight
                ? `Line height: ${activeLineHeight}`
                : "Line height (tight / normal / loose)"
            }
            onMouseDown={(e) => {
              e.preventDefault();
              cycleLineHeight();
            }}
          >
            <ALargeSmall size={14} strokeWidth={2} />
          </button>
        </div>
      </BubbleMenu>

      {/* TipTap editor content */}
      <EditorContent
        editor={editor}
        className={
          isDocumentMode
            ? "[&_.tiptap]:outline-none"
            : "[&_.tiptap]:outline-none [&_.tiptap_p]:m-0"
        }
      />
    </div>
  );
}
